import { useEffect, useMemo, useState } from 'react'
import Toast from '../components/Toast.jsx'
import Modal from '../components/Modal.jsx'
import BackButton from '../components/BackButton.jsx'
import { useAuth } from '../auth/AuthProvider.jsx'
import {
  markLoanAsLost,
  registerLoanReturn,
  sendLoanToMaintenance,
  returnFromMaintenance
} from '../data/movements.js'
import { fetchMovimentacoes } from '../data/syncManager.js'
import { getObrasAtivas } from '../data/obras.js'

function formatDate(ts) {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

export default function PendentesPage() {
  const { session } = useAuth()
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [items, setItems] = useState([])
  const [maintenanceItems, setMaintenanceItems] = useState([])
  const [busyId, setBusyId] = useState(null)
  const [tab, setTab] = useState('pendentes')

  // Filtros
  const [filtroFuncionario, setFiltroFuncionario] = useState('')
  const [filtroProduto, setFiltroProduto] = useState('')
  const [filtroDias, setFiltroDias] = useState('todos')
  const [filtroObra, setFiltroObra] = useState('')
  const [obras, setObras] = useState([])

  const [modal, setModal] = useState({ open: false, action: null, item: null })
  const [obs, setObs] = useState('')
  // Campos de quantidade para devolução parcial
  const [qtdDevolvida, setQtdDevolvida] = useState('')
  const [qtdManutencao, setQtdManutencao] = useState('')
  // Campos de manutenção (opcionais) para retorno
  const [manCusto, setManCusto] = useState('')
  const [manFornecedor, setManFornecedor] = useState('')
  const [manNF, setManNF] = useState('')
  const [manData, setManData] = useState(() => new Date().toISOString().slice(0,10))

  async function reload() {
    const [allMovs, obrasList] = await Promise.all([
      fetchMovimentacoes(),
      getObrasAtivas()
    ])
    setObras(obrasList)
    const pend = (allMovs || []).filter(m => m.status === 'pendente-devolucao')
    const maint = (allMovs || []).filter(m => m.status === 'em-manutencao')
    setItems(pend)
    setMaintenanceItems(maint)
  }

  useEffect(() => {
    reload()
  }, [session?.empresaId])

  const pendentesFiltrados = useMemo(() => {
    let result = [...items]
    
    // Filtro por funcionário
    if (filtroFuncionario.trim()) {
      const q = filtroFuncionario.toLowerCase()
      result = result.filter(m => m.funcionarioNome?.toLowerCase().includes(q))
    }
    
    // Filtro por produto
    if (filtroProduto.trim()) {
      const q = filtroProduto.toLowerCase()
      result = result.filter(m => m.produtoNome?.toLowerCase().includes(q))
    }
    
    // Filtro por dias
    if (filtroDias !== 'todos') {
      const dias = Number(filtroDias)
      const limite = Date.now() - (dias * 24 * 60 * 60 * 1000)
      result = result.filter(m => (m.dataMovimentacao ?? 0) < limite)
    }
    
    // Filtro por obra
    if (filtroObra) {
      result = result.filter(m => m.obraId === filtroObra)
    }
    
    return result.sort((a, b) => (b.dataMovimentacao ?? 0) - (a.dataMovimentacao ?? 0))
  }, [items, filtroFuncionario, filtroProduto, filtroDias, filtroObra])

  const emManutencaoFiltrados = useMemo(() => {
    let result = [...maintenanceItems]
    
    if (filtroFuncionario.trim()) {
      const q = filtroFuncionario.toLowerCase()
      result = result.filter(m => m.funcionarioNome?.toLowerCase().includes(q))
    }
    
    if (filtroProduto.trim()) {
      const q = filtroProduto.toLowerCase()
      result = result.filter(m => m.produtoNome?.toLowerCase().includes(q))
    }
    
    // Filtro por obra
    if (filtroObra) {
      result = result.filter(m => m.obraId === filtroObra)
    }
    
    return result.sort((a, b) => (b.dataEnvioManutencao ?? 0) - (a.dataEnvioManutencao ?? 0))
  }, [maintenanceItems, filtroFuncionario, filtroProduto, filtroObra])

  // Dados exibidos conforme aba
  const pendentes = pendentesFiltrados
  const emManutencao = emManutencaoFiltrados

  async function runAction(action, item) {
    setBusyId(item.id)
    try {
      if (action === 'devolver') {
        await registerLoanReturn({ 
          usuario: session, 
          movementId: item.id, 
          observacao: obs,
          quantidadeDevolvida: qtdDevolvida ? Number(qtdDevolvida) : null
        })
      } else if (action === 'perdido') {
        await markLoanAsLost({ usuario: session, movementId: item.id, observacao: obs })
      } else if (action === 'manutencao') {
        await sendLoanToMaintenance({ 
          usuario: session, 
          movementId: item.id, 
          observacao: obs,
          quantidadeManutencao: qtdManutencao ? Number(qtdManutencao) : null
        })
      } else if (action === 'retorno_manutencao') {
        await returnFromMaintenance({
          usuario: session,
          movementId: item.id,
          observacao: obs,
          manutencao: {
            custo: manCusto !== '' ? Number(manCusto) : null,
            fornecedor: manFornecedor || null,
            nf: manNF || null,
            dataServico: manData ? new Date(manData).getTime() : Date.now(),
            observacao: obs
          }
        })
      }

      setToastType('success')
      setToast('Atualizado com sucesso.')
      setModal({ open: false, action: null, item: null })
      setObs('')
      setQtdDevolvida('')
      setQtdManutencao('')
      setManCusto('')
      setManFornecedor('')
      setManNF('')
      setManData(new Date().toISOString().slice(0,10))
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao atualizar.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />

      <BackButton />

      <div className="rounded-2xl cm-card p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Pendências</div>
          <div className="text-xs text-white/70">{tab === 'pendentes' ? `${pendentes.length} pendentes` : `${emManutencao.length} em manutenção`}</div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => setTab('pendentes')}
            className={`rounded-xl px-3 py-2 text-xs font-semibold ${tab === 'pendentes' ? 'bg-accent-600 text-white' : 'bg-white/10'}`}
          >
            Pendentes
          </button>
          <button
            onClick={() => setTab('manutencao')}
            className={`rounded-xl px-3 py-2 text-xs font-semibold ${tab === 'manutencao' ? 'bg-accent-600 text-white' : 'bg-white/10'}`}
          >
            Em Manutenção
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="rounded-2xl cm-card p-3">
        <div className="text-xs font-semibold text-white/70 mb-2">🔍 Filtros</div>
        <div className="space-y-2">
          <input
            value={filtroFuncionario}
            onChange={(e) => setFiltroFuncionario(e.target.value)}
            className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none placeholder:text-white/40"
            placeholder="Buscar por funcionário..."
          />
          <input
            value={filtroProduto}
            onChange={(e) => setFiltroProduto(e.target.value)}
            className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none placeholder:text-white/40"
            placeholder="Buscar por produto..."
          />
          {tab === 'pendentes' && (
            <select
              value={filtroDias}
              onChange={(e) => setFiltroDias(e.target.value)}
              className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm"
            >
              <option value="todos">Todos os períodos</option>
              <option value="7">Mais de 7 dias</option>
              <option value="15">Mais de 15 dias</option>
              <option value="30">Mais de 30 dias</option>
              <option value="60">Mais de 60 dias</option>
            </select>
          )}
          {obras.length > 0 && (
            <select
              value={filtroObra}
              onChange={(e) => setFiltroObra(e.target.value)}
              className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm"
            >
              <option value="">Todas as obras</option>
              {obras.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome} ({o.codigo})
                </option>
              ))}
            </select>
          )}
        </div>
        {(filtroFuncionario || filtroProduto || filtroDias !== 'todos' || filtroObra) && (
          <button
            onClick={() => {
              setFiltroFuncionario('')
              setFiltroProduto('')
              setFiltroDias('todos')
              setFiltroObra('')
            }}
            className="mt-2 w-full rounded-xl bg-white/5 py-2 text-xs text-white/70"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {tab === 'pendentes' && (
        <>
          {pendentes.length === 0 ? (
            <div className="rounded-2xl cm-card p-4 text-sm text-white/80">Nenhuma pendência.</div>
          ) : (
            <div className="space-y-3">
              {pendentes.map((m) => (
                <div key={m.id} className="rounded-2xl cm-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{m.produtoNome}</div>
                      <div className="mt-1 text-xs text-white/70">
                        Funcionário: {m.funcionarioNome} • Qtd: {m.quantidade}{m.obraNome ? ` • 🏗️ ${m.obraNome}` : ''}
                      </div>
                      <div className="mt-1 text-xs text-white/70">Data: {formatDate(m.dataMovimentacao)}</div>
                      <div className="mt-1 text-xs text-white/70">
                        Status: <span className="font-semibold">{m.status}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <button
                      disabled={busyId === m.id}
                      className="rounded-xl bg-emerald-600/90 px-2 py-2 text-xs font-semibold disabled:opacity-60"
                      onClick={() => {
                        setObs('')
                        setModal({ open: true, action: 'devolver', item: m })
                      }}
                    >
                      Devolver
                    </button>
                    <button
                      disabled={busyId === m.id}
                      className="rounded-xl bg-amber-500/90 px-2 py-2 text-xs font-semibold disabled:opacity-60"
                      onClick={() => {
                        setObs('')
                        setModal({ open: true, action: 'manutencao', item: m })
                      }}
                    >
                      Manutenção
                    </button>
                    <button
                      disabled={busyId === m.id}
                      className="rounded-xl bg-red-600/90 px-2 py-2 text-xs font-semibold disabled:opacity-60"
                      onClick={() => {
                        setObs('')
                        setModal({ open: true, action: 'perdido', item: m })
                      }}
                    >
                      Perdido
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'manutencao' && (
        <>
          {emManutencao.length === 0 ? (
            <div className="rounded-2xl cm-card p-4 text-sm text-white/80">Nenhum item em manutenção.</div>
          ) : (
            <div className="space-y-3">
              {emManutencao.map((m) => (
                <div key={m.id} className="rounded-2xl cm-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{m.produtoNome}</div>
                      <div className="mt-1 text-xs text-white/70">
                        Funcionário: {m.funcionarioNome} • Qtd: {m.quantidade}{m.obraNome ? ` • 🏗️ ${m.obraNome}` : ''}
                      </div>
                      <div className="mt-1 text-xs text-white/70">Enviado: {formatDate(m.dataEnvioManutencao)}</div>
                      <div className="mt-1 text-xs text-white/70">
                        Status: <span className="font-semibold text-amber-400">{m.status}</span>
                      </div>
                      {m.observacaoDevolucao ? (
                        <div className="mt-1 text-xs text-white/60">Motivo: {m.observacaoDevolucao}</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-3">
                    <button
                      disabled={busyId === m.id}
                      className="w-full rounded-xl bg-emerald-600/90 px-3 py-2 text-xs font-semibold disabled:opacity-60"
                      onClick={() => {
                        setObs('')
                        setModal({ open: true, action: 'retorno_manutencao', item: m })
                      }}
                    >
                      Retornou da manutenção (voltar ao estoque)
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        open={modal.open}
        title={
          modal.action === 'devolver'
            ? 'Confirmar devolução'
            : modal.action === 'manutencao'
              ? 'Enviar para manutenção'
              : modal.action === 'retorno_manutencao'
                ? 'Retorno da manutenção'
                : 'Marcar como perdido'
        }
        onClose={() => {
          setModal({ open: false, action: null, item: null })
          setQtdDevolvida('')
          setQtdManutencao('')
        }}
      >
        <div className="text-sm font-semibold">{modal.item?.produtoNome}</div>
        <div className="mt-1 text-xs text-white/70">Funcionário: {modal.item?.funcionarioNome} • Qtd total: {modal.item?.quantidade}{modal.item?.obraNome ? ` • 🏗️ ${modal.item.obraNome}` : ''}</div>

        {modal.action === 'devolver' && modal.item?.quantidade > 1 && (
          <div className="mt-4 space-y-3">
            <div className="text-xs font-semibold text-white/70">Quantidade devolvida</div>
            <div>
              <div className="text-[10px] text-white/60">Devolvida</div>
              <input
                type="number"
                min="0"
                max={modal.item?.quantidade}
                value={qtdDevolvida}
                onChange={(e) => setQtdDevolvida(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none"
                placeholder={modal.item?.quantidade}
              />
            </div>
            <div className="text-[10px] text-white/50">
              Deixar pendente: {modal.item?.quantidade - Number(qtdDevolvida || modal.item?.quantidade)}
            </div>
          </div>
        )}

        {modal.action === 'manutencao' && modal.item?.quantidade > 1 && (
          <div className="mt-4 space-y-3">
            <div className="text-xs font-semibold text-white/70">Quantidade para manutenção</div>
            <div>
              <div className="text-[10px] text-white/60">Enviar para manutenção</div>
              <input
                type="number"
                min="0"
                max={modal.item?.quantidade}
                value={qtdManutencao}
                onChange={(e) => setQtdManutencao(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none"
                placeholder="0"
              />
            </div>
            <div className="text-[10px] text-white/50">
              Deixar pendente: {modal.item?.quantidade - Number(qtdManutencao || 0)}
            </div>
          </div>
        )}

        <div className="mt-4">
          <div className="text-xs text-white/70">Observação (opcional)</div>
          <input
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
            placeholder="Ex: quebrou / não localizado / enviado oficina"
          />
        </div>

        {modal.action === 'retorno_manutencao' && (
          <div className="mt-4 space-y-3">
            <div className="text-xs font-semibold text-white/70">Custo de manutenção (opcional)</div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={manCusto}
                onChange={e => setManCusto(e.target.value)}
                className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
                placeholder="R$ 0,00"
              />
              <input
                type="date"
                value={manData}
                onChange={e => setManData(e.target.value)}
                className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              />
            </div>
            <input
              value={manFornecedor}
              onChange={e => setManFornecedor(e.target.value)}
              className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              placeholder="Fornecedor (opcional)"
            />
            <input
              value={manNF}
              onChange={e => setManNF(e.target.value)}
              className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              placeholder="Nº NF/OS (opcional)"
            />
          </div>
        )}

        <button
          disabled={!modal.item || busyId === modal.item?.id}
          onClick={() => runAction(modal.action, modal.item)}
          className="mt-4 w-full rounded-xl bg-accent-600 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
        >
          Confirmar
        </button>
      </Modal>
    </div>
  )
}
